import seed from '@content/content.seed.json'
import type { SiteContent } from './content.types'

/**
 * API 베이스 URL.
 * - 개발: 비워두면 vite dev 프록시가 localhost:4000 으로 넘긴다.
 * - 운영: .env 의 VITE_API_BASE_URL 에 API 도메인을 넣는다.
 *   (예: https://api.annx.ai — S3/CloudFront와 API 서버가 다른 도메인일 때)
 */
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

/** API가 아직 없거나 응답하지 않을 때 사용하는 기본 콘텐츠 */
export const seedContent = seed as unknown as SiteContent

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    // 관리자 세션은 httpOnly 쿠키로 유지되므로 항상 쿠키를 함께 보낸다.
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    let message = res.statusText
    let code: string | undefined
    try {
      const body = (await res.json()) as { message?: string; code?: string }
      message = body.message ?? message
      code = body.code
    } catch {
      /* 본문이 JSON이 아니면 statusText를 그대로 쓴다 */
    }
    throw new ApiError(message, res.status, code)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/* ── 공개 API ─────────────────────────────────────────────── */

/** 랜딩페이지 콘텐츠 조회. 실패하면 seed 콘텐츠로 폴백한다. */
export async function getContent(): Promise<{ content: SiteContent; fromApi: boolean }> {
  try {
    const content = await request<SiteContent>('/api/content')
    return { content, fromApi: true }
  } catch {
    return { content: seedContent, fromApi: false }
  }
}

/* ── 관리자 API ───────────────────────────────────────────── */

export interface AdminSession {
  authenticated: boolean
}

export function login(password: string) {
  return request<AdminSession>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  })
}

export function logout() {
  return request<void>('/api/auth/logout', { method: 'POST' })
}

export function getSession() {
  return request<AdminSession>('/api/auth/session')
}

/** 콘텐츠 저장. version이 서버와 다르면 409를 던진다(동시 편집 충돌). */
export function saveContent(content: SiteContent) {
  return request<SiteContent>('/api/content', {
    method: 'PUT',
    body: JSON.stringify(content),
  })
}

export interface UploadResult {
  /** 저장된 파일의 공개 경로 (예: /uploads/2026/07/abc123.webp) */
  url: string
  filename: string
  mimeType: string
  size: number
  kind: 'image' | 'video'
}

/** 진행률을 보고하는 파일 업로드 (fetch는 업로드 진행률을 못 주므로 XHR 사용) */
function uploadFile<T>(path: string, file: File, onProgress?: (pct: number) => void) {
  const form = new FormData()
  form.append('file', file)

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}${path}`)
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as T)
      } else {
        let message = xhr.statusText
        try {
          message = (JSON.parse(xhr.responseText) as { message?: string }).message ?? message
        } catch {
          /* noop */
        }
        reject(new ApiError(message, xhr.status))
      }
    }
    xhr.onerror = () => reject(new ApiError('네트워크 오류로 업로드에 실패했습니다.', 0))
    xhr.send(form)
  })
}

/** 이미지/영상 업로드 */
export function uploadMedia(file: File, onProgress?: (pct: number) => void) {
  return uploadFile<UploadResult>('/api/media', file, onProgress)
}

/* ── 업로드 HTML 페이지 (Studio 갤러리 카드 링크) ─────────── */

export interface StoredPage {
  /** 새 탭으로 열 주소. 콘텐츠의 gallery[].href 에 그대로 넣는 값 */
  url: string
  filename: string
  /** HTML의 <title> (없으면 파일명) */
  title: string
  size: number
  uploadedAt: string
}

/** HTML 파일 업로드 */
export function uploadPage(file: File, onProgress?: (pct: number) => void) {
  return uploadFile<StoredPage>('/api/pages', file, onProgress)
}

/** 등록된 HTML 페이지 목록 */
export function listPages() {
  return request<{ items: StoredPage[] }>('/api/pages')
}

/** HTML 페이지 삭제 */
export function deletePage(url: string) {
  return request<void>(`/api/pages?url=${encodeURIComponent(url)}`, { method: 'DELETE' })
}

/** `/pages/...` 상대 경로를 실제로 열 수 있는 URL로 바꾼다 */
export function resolvePageUrl(href: string): string {
  if (!href) return ''
  if (/^(https?:)?\/\//.test(href)) return href
  if (href.startsWith('/pages/')) return `${API_BASE}${href}`
  return href
}

/**
 * 뷰어 접근 티켓을 받아온다.
 *
 * 서버는 요청한 브라우저에서만, 짧은 시간 동안 유효한 티켓을 붙인 URL을 돌려준다.
 * 링크를 복사해 남에게 보내거나 나중에 다시 열면 열리지 않는다.
 *
 * @param urls 콘텐츠에 저장된 `/pages/...` 경로들
 * @returns 원본 경로 → 티켓이 붙은 URL 매핑. 실패하면 빈 객체(원본 링크로 폴백)
 */
export async function fetchPageTickets(urls: string[]): Promise<{
  tickets: Record<string, string>
  ttlSeconds: number
}> {
  if (urls.length === 0) return { tickets: {}, ttlSeconds: 0 }
  try {
    return await request<{ tickets: Record<string, string>; ttlSeconds: number }>(
      '/api/pages/tickets',
      { method: 'POST', body: JSON.stringify({ urls }) },
    )
  } catch {
    return { tickets: {}, ttlSeconds: 0 }
  }
}

/**
 * 브로슈어가 PDF인지 판정한다.
 *
 * PDF는 화면에 띄우지 않고 **파일로 내려받게** 한다(서버가 `Content-Disposition: attachment`
 * 로 내려보낸다). HTML이면 `/brochure` 화면에서 진행률과 함께 iframe에 담는다.
 * 판정은 확장자로 한다 — 외부 주소는 미리 열어보고 확인할 방법이 없기 때문이다.
 */
export function isPdfHref(href: string): boolean {
  return /\.pdf(\?|#|$)/i.test(href.trim())
}

/**
 * 업로드한 파일(`/pages/...`)이면 접근 티켓을 붙인 최종 주소를, 아니면 원본 주소를 돌려준다.
 * 티켓 발급에 실패하면 원본 경로로 폴백한다(서버가 요구하면 안내 페이지가 뜬다).
 */
export async function resolveWithTicket(href: string): Promise<string> {
  if (!href.startsWith('/pages/')) return resolvePageUrl(href)
  const { tickets } = await fetchPageTickets([href])
  return resolvePageUrl(tickets[href] ?? href)
}
